import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useId, useState, type JSX, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Select } from '@/components/ui/Select';
import { formatDate } from '@/features/tenants/module-labels';
import { statusLabel } from '@/features/tenants/tenant-labels';
import { downloadText, toCsv, type CsvValue } from '@/lib/csv';
import { formatMoney, formatPercent } from '@/lib/money';

import styles from './ReportsPage.module.scss';
import {
  getPlatformReports,
  reportsQueryKey,
  type MonthlyPoint,
  type PlatformReports,
} from '../api/reports.graphql';

/** OS-1726 — definición de cada métrica, visible en la UI (AC: "cada reporte cita su definición"). */
export const REPORT_DEFINITIONS = {
  adoption:
    'Habilitados: tenants no cancelados con el módulo habilitado (los módulos base cuentan en todos). ' +
    'Cobrados: tenants con una línea de suscripción vigente del módulo. Con uso: tenants con actividad ' +
    'del módulo en los últimos 30 días según el resumen diario de uso; solo Nómina (archivos PILA), ' +
    'Asistencia (empleados con marcación) y Cocina (órdenes KDS) tienen esa señal, el resto dice "Sin señal".',
  mrrByTenant:
    'MRR real: suma de las líneas de suscripción vigentes de los tenants activos o en mora (precio ' +
    'contratado menos descuento). MRR potencial: lo mismo para tenants en piloto o prueba. Es el ' +
    'mismo cálculo del dashboard; suspendidos y cancelados no suman.',
  mrrByModule:
    'MRR real agrupado por el módulo de cada línea vigente. La suma de la tabla es igual al MRR real total.',
  monthly:
    'MRR al cierre de cada mes (el mes en curso, a hoy), con el estado de cada tenant reconstruido del ' +
    'historial de ciclo de vida. En pago: tenants activos o en mora al cierre. Conversión: conversiones a ' +
    'pago ÷ (conversiones + pilotos cancelados) del mes. Churn: tenants cancelados que venían activos, en ' +
    'mora o suspendidos ÷ tenants en esos estados al inicio del mes. Mes en hora Colombia. ' +
    'Historial incompleto: antes de la primera línea de suscripción no hay MRR registrado, y antes del ' +
    'primer cambio de estado registrado se supone el estado actual; esos meses se marcan.',
  usage:
    'DAU promedio: promedio diario de usuarios distintos con sesión, en los días con registro de los ' +
    'últimos 30. Días activos: días con al menos un usuario. Fuente: resumen diario de uso (job ' +
    'nocturno); nunca datos de negocio en vivo ni biométricos. Las sedes activas aún no se registran.',
} as const;

const MONTH_OPTIONS = [3, 6, 12, 24] as const;

function exportCsv(name: string, header: string[], rows: CsvValue[][]): void {
  downloadText(`${name}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(header, rows));
}

const COVERAGE_TEXT: Readonly<Record<'partial' | 'none', string>> = {
  partial: 'parcial',
  none: 'sin registro',
};

/** OS-1724 — aviso de historial incompleto del mes (null si está completo). */
export function coverageNote(point: MonthlyPoint, canSeeBilling: boolean): string | null {
  const parts: string[] = [];
  if (canSeeBilling && point.mrrCoverage !== 'complete') {
    parts.push(`MRR ${COVERAGE_TEXT[point.mrrCoverage]}`);
  }
  if (point.lifecycleCoverage !== 'complete') {
    parts.push(`estados ${COVERAGE_TEXT[point.lifecycleCoverage]}`);
  }
  return parts.length > 0 ? `Historial incompleto: ${parts.join(', ')}` : null;
}

function rate(value: number | null): string {
  return value === null ? '—' : formatPercent(value);
}

interface SectionProps {
  id: string;
  title: string;
  definition: string;
  onExport?: () => void;
  children: ReactNode;
}

function ReportSection({ id, title, definition, onExport, children }: SectionProps): JSX.Element {
  return (
    <section className={styles.section} aria-labelledby={id}>
      <div className={styles.sectionHeader}>
        <h2 id={id} className={styles.sectionTitle}>
          {title}
        </h2>
        {onExport ? (
          <button type="button" className={styles.exportBtn} onClick={onExport} aria-label={`Exportar ${title} (CSV)`}>
            <Download size={14} aria-hidden="true" />
            CSV
          </button>
        ) : null}
      </div>
      <p className={styles.definition}>
        <strong>Definición:</strong> {definition}
      </p>
      <div className={styles.tableWrap}>{children}</div>
    </section>
  );
}

function Reports({ data, idPrefix }: { data: PlatformReports; idPrefix: string }): JSX.Element {
  const { currency } = data;
  const moduleNames = new Map(data.adoption.map((module) => [module.moduleKey, module.name]));
  return (
    <>
      <ReportSection
        id={`${idPrefix}-adoption`}
        title="Adopción por módulo"
        definition={REPORT_DEFINITIONS.adoption}
        onExport={() =>
          exportCsv(
            'adopcion-modulos',
            ['modulo', 'habilitados', 'cobrados', 'con_uso_30d', 'senal_de_uso', 'tenants'],
            data.adoption.map((m) => [m.name, m.enabledTenants, m.billedTenants, m.usedTenants ?? '', m.usageSignal ?? '', data.totalTenants]),
          )
        }
      >
        <table className={styles.table} aria-labelledby={`${idPrefix}-adoption`}>
          <thead>
            <tr>
              <th scope="col">Módulo</th>
              <th scope="col">Habilitados</th>
              <th scope="col">Cobrados</th>
              <th scope="col">Con uso (30 días)</th>
            </tr>
          </thead>
          <tbody>
            {data.adoption.map((module) => (
              <tr key={module.moduleKey}>
                <th scope="row">{module.name}</th>
                <td>
                  {module.enabledTenants} de {data.totalTenants}
                </td>
                <td>{module.billedTenants}</td>
                <td>
                  {module.usedTenants === null ? (
                    <span className={styles.muted}>Sin señal</span>
                  ) : (
                    <>
                      {module.usedTenants}
                      <span className={styles.muted}> ({module.usageSignal})</span>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportSection>

      <ReportSection
        id={`${idPrefix}-monthly`}
        title="Evolución mensual: MRR, conversión y churn"
        definition={REPORT_DEFINITIONS.monthly}
        onExport={() =>
          exportCsv(
            'evolucion-mensual',
            ['mes', `mrr_${currency.toLowerCase()}`, 'en_pago', 'conversiones', 'pilotos_cancelados', 'conversion_pct', 'churn', 'churn_pct', 'cobertura_mrr', 'cobertura_estados'],
            data.monthly.map((p) => [
              p.month,
              p.mrr === null ? '' : p.mrr.toFixed(2),
              p.payingTenants,
              p.conversions,
              p.pilotsCancelled,
              p.conversionRate ?? '',
              p.churnedTenants,
              p.churnRate ?? '',
              p.mrrCoverage,
              p.lifecycleCoverage,
            ]),
          )
        }
      >
        <table className={styles.table} aria-labelledby={`${idPrefix}-monthly`}>
          <thead>
            <tr>
              <th scope="col">Mes</th>
              {data.canSeeBilling ? <th scope="col">MRR ({currency})</th> : null}
              <th scope="col">En pago</th>
              <th scope="col">Conversiones</th>
              <th scope="col">Conversión</th>
              <th scope="col">Churn</th>
              <th scope="col">Tasa de churn</th>
            </tr>
          </thead>
          <tbody>
            {data.monthly.map((point) => (
              <tr key={point.month}>
                <th scope="row">
                  {point.month}
                  {coverageNote(point, data.canSeeBilling) ? (
                    <span className={styles.coverage}>{coverageNote(point, data.canSeeBilling)}</span>
                  ) : null}
                </th>
                {data.canSeeBilling ? <td>{formatMoney(point.mrr, currency)}</td> : null}
                <td>{point.payingTenants}</td>
                <td>
                  {point.conversions}
                  {point.pilotsCancelled > 0 ? (
                    <span className={styles.muted}> · {point.pilotsCancelled} pilotos cancelados</span>
                  ) : null}
                </td>
                <td>{rate(point.conversionRate)}</td>
                <td>{point.churnedTenants}</td>
                <td>{rate(point.churnRate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportSection>

      {data.mrrByTenant ? (
        <ReportSection
          id={`${idPrefix}-mrr-tenant`}
          title="MRR por tenant"
          definition={REPORT_DEFINITIONS.mrrByTenant}
          onExport={() =>
            exportCsv(
              'mrr-por-tenant',
              ['tenant_id', 'tenant', 'estado', `mrr_${currency.toLowerCase()}`, `mrr_potencial_${currency.toLowerCase()}`],
              (data.mrrByTenant ?? []).map((row) => [row.tenantId, row.name, row.planStatus, row.mrr.toFixed(2), row.potentialMrr.toFixed(2)]),
            )
          }
        >
          <table className={styles.table} aria-labelledby={`${idPrefix}-mrr-tenant`}>
            <thead>
              <tr>
                <th scope="col">Tenant</th>
                <th scope="col">Estado</th>
                <th scope="col">MRR</th>
                <th scope="col">MRR potencial</th>
              </tr>
            </thead>
            <tbody>
              {data.mrrByTenant.map((row) => (
                <tr key={row.tenantId}>
                  <th scope="row">
                    <Link to={`/tenants/${row.tenantId}?tab=billing`}>{row.name}</Link>
                  </th>
                  <td>{statusLabel(row.planStatus)}</td>
                  <td>{formatMoney(row.mrr, currency)}</td>
                  <td>{row.potentialMrr > 0 ? formatMoney(row.potentialMrr, currency) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </ReportSection>
      ) : null}

      {data.mrrByModule ? (
        <ReportSection
          id={`${idPrefix}-mrr-module`}
          title="MRR por módulo"
          definition={REPORT_DEFINITIONS.mrrByModule}
          onExport={() =>
            exportCsv(
              'mrr-por-modulo',
              ['modulo', `mrr_${currency.toLowerCase()}`],
              (data.mrrByModule ?? []).map((row) => [moduleNames.get(row.moduleKey) ?? row.moduleKey, row.mrr.toFixed(2)]),
            )
          }
        >
          <table className={styles.table} aria-labelledby={`${idPrefix}-mrr-module`}>
            <thead>
              <tr>
                <th scope="col">Módulo</th>
                <th scope="col">MRR</th>
              </tr>
            </thead>
            <tbody>
              {data.mrrByModule.length === 0 ? (
                <tr>
                  <td colSpan={2} className={styles.muted}>
                    Ningún tenant activo tiene módulos en su suscripción.
                  </td>
                </tr>
              ) : (
                data.mrrByModule.map((row) => (
                  <tr key={row.moduleKey}>
                    <th scope="row">{moduleNames.get(row.moduleKey) ?? row.moduleKey}</th>
                    <td>{formatMoney(row.mrr, currency)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ReportSection>
      ) : null}

      <ReportSection
        id={`${idPrefix}-usage`}
        title={`Uso agregado (últimos ${data.usageDays} días)`}
        definition={REPORT_DEFINITIONS.usage}
        onExport={() =>
          exportCsv(
            'uso-por-tenant',
            ['tenant_id', 'tenant', 'estado', 'dau_promedio', 'dias_activos', 'ultima_actividad'],
            data.usage.map((row) => [row.tenantId, row.name, row.planStatus, row.avgDau, row.activeDays, row.lastActivity?.slice(0, 10) ?? '']),
          )
        }
      >
        <table className={styles.table} aria-labelledby={`${idPrefix}-usage`}>
          <thead>
            <tr>
              <th scope="col">Tenant</th>
              <th scope="col">Estado</th>
              <th scope="col">DAU promedio</th>
              <th scope="col">Días activos</th>
              <th scope="col">Última actividad</th>
            </tr>
          </thead>
          <tbody>
            {data.usage.map((row) => (
              <tr key={row.tenantId}>
                <th scope="row">{row.name}</th>
                <td>{statusLabel(row.planStatus)}</td>
                <td>{row.avgDau.toLocaleString('es-CO')}</td>
                <td>
                  {row.activeDays} / {data.usageDays}
                </td>
                <td>{row.lastActivity ? formatDate(row.lastActivity) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ReportSection>
    </>
  );
}

/**
 * OS-1726 — `/reports`: reportes cross-tenant con su definición y exportación CSV. Los montos solo
 * aparecen con `platform.billing.read` (el API los manda en null para SOPORTE).
 */
export function ReportsPage(): JSX.Element {
  const idPrefix = useId();
  const [months, setMonths] = useState<number>(6);
  const query = useQuery({ queryKey: reportsQueryKey(months), queryFn: () => getPlatformReports(months) });

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Reportes</h1>
          <p className={styles.muted}>
            Comparativos entre tenants a partir de agregados. Montos en COP.
            {query.data && !query.data.canSeeBilling ? ' Tu rol no ve montos de facturación.' : ''}
          </p>
        </div>
        <label className={styles.monthsField} htmlFor={`${idPrefix}-months`}>
          Meses
          <Select
            id={`${idPrefix}-months`}
            value={String(months)}
            onChange={(value) => setMonths(Number(value))}
            options={MONTH_OPTIONS.map((option) => ({ value: String(option), label: `Últimos ${option}` }))}
          />
        </label>
      </div>
      {query.isLoading ? <p className={styles.muted}>Calculando reportes…</p> : null}
      {query.isError ? (
        <p className={styles.error} role="alert">
          No se pudieron cargar los reportes: {query.error.message}
        </p>
      ) : null}
      {query.data ? <Reports data={query.data} idPrefix={idPrefix} /> : null}
    </div>
  );
}
